import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { AutoTextarea } from '../components/AutoTextarea';
import { DictateButton } from '../components/DictateButton';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useUserOrg } from '../lib/org';
import { getSite, createDocument, getDocument, type Site } from '../lib/db';
import { uploadDocument } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { DOC_TEMPLATES } from '../data/docTemplates';
import { helpFor } from '../data/cppHelp';
import { generateCppPdf } from '../lib/pdf';
import { LoadingIndicator } from '../components/LoadingIndicator';

const REVIEW_CHECKS = [
  'Site conditions reviewed — physical conditions match the assumed scope of works',
  'Sub-contractor competence verified — every named contractor holds relevant cards / training',
  'Existing services confirmed — gas / electric / water / drainage drawings reviewed and CAT-scanned where relevant',
  'Sections adapted to this specific site — generic standard text replaced where it does not fit',
  'I am competent to prepare and sign off this Construction Phase Plan — I have the relevant training, experience and authority',
];

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

interface CppSectionTickListProps {
  sectionIdx: number;
  items: string[];
  checks: boolean[];
  customValue: string;
  onToggle: (sectionIdx: number, itemIdx: number) => void;
  onRemove: (sectionIdx: number, itemIdx: number) => void;
  onAdd: (sectionIdx: number) => void;
  onCustomChange: (value: string) => void;
}

/**
 * Tick-list renderer for CPP sections that supply an items[] in their
 * template. Mirrors SectionTickList in DocumentFillForm.tsx, locally
 * declared to keep CPP self-contained.
 */
function CppSectionTickList({
  sectionIdx, items, checks, customValue,
  onToggle, onRemove, onAdd, onCustomChange,
}: CppSectionTickListProps) {
  return (
    <>
      {items.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
          No items yet — add one below.
        </p>
      ) : (
        <div className="check-list">
          {items.map((item, i) => (
            <label key={`${i}-${item}`} className="check-row">
              <input
                type="checkbox"
                checked={checks[i] ?? false}
                onChange={() => onToggle(sectionIdx, i)}
              />
              <span style={{ flex: 1 }}>{item}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onRemove(sectionIdx, i); }}
                aria-label={`Remove ${item}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--ink-soft)', fontSize: 18, padding: '0 4px',
                  cursor: 'pointer',
                }}
              >×</button>
            </label>
          ))}
        </div>
      )}
      <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Add another clause for this site…"
            value={customValue}
            onChange={(e) => onCustomChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onAdd(sectionIdx); }
            }}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onAdd(sectionIdx)}
            disabled={!customValue.trim()}
            style={{ flex: '0 0 auto', padding: '0 16px' }}
          >+ Add</button>
        </div>
      </div>
    </>
  );
}

type SectionStatus = 'standard' | 'edited' | 'todo' | 'na';

const NA_TEXT = 'Not applicable to this project.';

export function CppFillForm() {
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams] = useSearchParams();
  const supersedeId = searchParams.get('supersede');
  const nav = useNavigate();
  const { org } = useUserOrg();
  const docIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);

  const template = DOC_TEMPLATES.find((t) => t.id === 'construction_phase_plan');

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [sectionBodies, setSectionBodies] = useState<string[]>([]);
  const [sectionStatuses, setSectionStatuses] = useState<SectionStatus[]>([]);
  const [openSection, setOpenSection] = useState<number | null>(0);
  // Tick-first: per-section selectable items for sections that have a
  // template `items[]`. Mirrors DocumentFillForm's pattern.
  const [sectionItems, setSectionItems] = useState<Record<number, string[]>>({});
  const [sectionItemChecks, setSectionItemChecks] = useState<Record<number, boolean[]>>({});
  const [sectionItemCustom, setSectionItemCustom] = useState<Record<number, string>>({});
  const [signatoryName, setSignatoryName] = useState('');
  const [notes, setNotes] = useState('');
  const [reviewChecks, setReviewChecks] = useState<boolean[]>([false, false, false, false, false]);

  function failWith(msg: string) {
    setError(msg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (!template) return;
        const initFields: Record<string, string> = {};
        for (const f of template.fields) initFields[f.key] = '';
        // Auto-prefill from site + org
        if (siteId) {
          const s = await getSite(siteId);
          setSite(s);
          if (s) {
            initFields.project_name = s.name;
            if (s.client_name) initFields.client = s.client_name;
            if (s.principal_designer) initFields.pd_name = s.principal_designer;
            if (s.address) initFields.site_address = s.address;
            if (s.site_manager) initFields.site_manager = s.site_manager;
          }
        }
        // Pre-fill PC name from auth user / org
        const { data } = await supabase.auth.getUser();
        const userName = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
        if (userName) setSignatoryName(userName);
        // PC company name from org
        // (org_name is captured separately in the PDF, but pre-fill the field in case the org IS the PC)
        // Will leave blank for the user to confirm.

        setFieldValues(initFields);
        // Free-text sections start EMPTY — the template body is only ever a
        // placeholder, so prompt text can never end up in the issued PDF.
        setSectionBodies(template.sections.map(() => ''));
        // All sections start as 'todo' — user must explicitly accept "use standard" or edit
        setSectionStatuses(template.sections.map(() => 'todo' as SectionStatus));
        // Seed tick-first sections (where the template defines items[]).
        const itemsByIdx: Record<number, string[]> = {};
        const checksByIdx: Record<number, boolean[]> = {};
        const customByIdx: Record<number, string> = {};
        template.sections.forEach((s, i) => {
          if (s.items && s.items.length > 0) {
            itemsByIdx[i] = s.items.slice();
            checksByIdx[i] = s.items.map(() => true);
            customByIdx[i] = '';
          }
        });
        setSectionItems(itemsByIdx);
        setSectionItemChecks(checksByIdx);
        setSectionItemCustom(customByIdx);

        if (supersedeId) {
          const prev = await getDocument(supersedeId);
          if (prev?.notes) setNotes(prev.notes);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, supersedeId, template]);

  if (loading) return <AppShell title="Loading…"><LoadingIndicator /></AppShell>;
  if (!template) return <AppShell title="CPP (Construction Phase Plan)"><div className="banner banner-error">CPP (Construction Phase Plan) template not found.</div></AppShell>;

  function setField(key: string, value: string) {
    setFieldValues((p) => ({ ...p, [key]: value }));
  }

  function setSectionBody(idx: number, val: string) {
    setSectionBodies((p) => p.map((b, i) => (i === idx ? val : b)));
    setSectionStatuses((p) => p.map((s, i) => (i === idx ? 'edited' : s)));
  }

  // Tick-section handlers — keep section-status in sync so the progress
  // counter ("12 of 17 reviewed") reflects any tick interaction.
  function toggleSectionItem(sectionIdx: number, itemIdx: number) {
    setSectionItemChecks((prev) => {
      const cur = prev[sectionIdx] ?? [];
      const next = cur.slice();
      next[itemIdx] = !next[itemIdx];
      return { ...prev, [sectionIdx]: next };
    });
    setSectionStatuses((p) => p.map((s, i) => (i === sectionIdx ? 'edited' : s)));
  }
  function removeSectionItem(sectionIdx: number, itemIdx: number) {
    setSectionItems((prev) => ({
      ...prev,
      [sectionIdx]: (prev[sectionIdx] ?? []).filter((_, j) => j !== itemIdx),
    }));
    setSectionItemChecks((prev) => ({
      ...prev,
      [sectionIdx]: (prev[sectionIdx] ?? []).filter((_, j) => j !== itemIdx),
    }));
    setSectionStatuses((p) => p.map((s, i) => (i === sectionIdx ? 'edited' : s)));
  }
  function addSectionItem(sectionIdx: number) {
    const text = (sectionItemCustom[sectionIdx] ?? '').trim();
    if (!text) return;
    setSectionItems((prev) => ({
      ...prev,
      [sectionIdx]: [...(prev[sectionIdx] ?? []), text],
    }));
    setSectionItemChecks((prev) => ({
      ...prev,
      [sectionIdx]: [...(prev[sectionIdx] ?? []), true],
    }));
    setSectionItemCustom((prev) => ({ ...prev, [sectionIdx]: '' }));
    setSectionStatuses((p) => p.map((s, i) => (i === sectionIdx ? 'edited' : s)));
  }

  // Serialise a tick-driven section to a bulleted text string the PDF
  // generator expects. Matches DocumentFillForm.tsx behaviour.
  function sectionBodyFromTicks(sectionIdx: number): string {
    const items = sectionItems[sectionIdx] ?? [];
    const checks = sectionItemChecks[sectionIdx] ?? [];
    return items
      .filter((_, i) => checks[i])
      .map((t) => `• ${t}`)
      .join('\n');
  }

  function markStandard(idx: number) {
    setSectionStatuses((p) => p.map((s, i) => (i === idx ? 'standard' : s)));
    setOpenSection(idx + 1 < template!.sections.length ? idx + 1 : null);
  }
  function markNa(idx: number) {
    setSectionStatuses((p) => p.map((s, i) => (i === idx ? 'na' : s)));
    setOpenSection(idx + 1 < template!.sections.length ? idx + 1 : null);
  }
  /** What actually goes into the PDF for section i. Never the prompt text. */
  function finalBody(i: number): string {
    const sec = template!.sections[i];
    if (sectionStatuses[i] === 'na') return NA_TEXT;
    if (sec.items && sec.items.length > 0) return sectionBodyFromTicks(i) || NA_TEXT;
    return (sectionBodies[i] ?? '').trim() || NA_TEXT;
  }
  const descriptionMissing = !(sectionBodies[0] ?? '').trim();

  const sectionsCount = template.sections.length;
  const reviewedCount = sectionStatuses.filter((s) => s !== 'todo').length;
  const progressPct = Math.round((reviewedCount / sectionsCount) * 100);

  const requiredFields = template.fields.filter((f) => f.required);
  const fieldsMissing = requiredFields.filter((f) => !(fieldValues[f.key] ?? '').trim());
  const allFieldsDone = fieldsMissing.length === 0;
  const allSectionsDone = sectionStatuses.every((s) => s !== 'todo');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!template) return;
    if (!org) { failWith('Organisation not loaded yet. Wait a moment and try again.'); return; }
    if (!allFieldsDone) {
      failWith(`Fill in: ${fieldsMissing.map((f) => f.label).join(', ')}.`);
      return;
    }
    if (descriptionMissing) {
      setOpenSection(0);
      failWith('Write the project description (section 1) — what is being built, where, and the key dates.');
      return;
    }
    if (!signatoryName.trim()) {
      failWith('Signatory name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const docId = docIdRef.current;
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

      let signatureDataUrl: string | null = null;
      if (sigRef.current && !sigRef.current.isEmpty()) {
        signatureDataUrl = await blobToDataUrl(await sigRef.current.toBlob());
      }

      const blob = await generateCppPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        fields: template.fields.map((f) => ({ key: f.key, label: f.label, value: fieldValues[f.key] ?? '' })),
        sections: template.sections.map((s, i) => ({
          heading: s.heading,
          body: finalBody(i),
        })),
        reviewChecks: REVIEW_CHECKS.map((label, i) => ({ label, checked: reviewChecks[i] ?? false })),
        signatureDataUrl,
        signatoryName: signatoryName.trim(),
        date: dateStr,
        notes: notes.trim() || null,
      });

      const fileName = `cpp-${(site?.name ?? 'site').replace(/\s+/g, '-')}-${dateStr.replace(/\s+/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const path = await uploadDocument(org.org_id, docId, file);

      await createDocument({
        id: docId,
        orgId: org.org_id,
        siteId: siteId ?? null,
        templateId: template.id,
        title: template.title,
        category: template.category,
        scope: template.scope,
        status: 'issued',
        filePath: path,
        fileName,
        mimeType: 'application/pdf',
        sizeBytes: file.size,
        notes: notes.trim() || null,
        supersedeId,
      });

      // ?share=1 → ShareModal auto-opens on the CPP's view page so the
      // user can immediately email it to whoever needs a copy.
      nav(`/documents/${docIdRef.current}?share=1`);
    } catch (e) {
      console.error('[CppFillForm] save failed:', e);
      failWith((e instanceof Error ? e.message : String(e)) + ' — check browser console.');
      setSubmitting(false);
    }
  }

  return (
    <AppShell title="Construction Phase Plan" back="/documents">
      {/* Intro card — title is already in the AppShell header (we use
          a shorter version up there), so we don't repeat it here. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {template.description}
        </p>
        {site && (
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
            Site: <strong>{site.name}</strong>
            {supersedeId && ' · creating new version'}
          </p>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <div className="cpp-progress">
        <div className="cpp-progress-bar">
          <div className="cpp-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="cpp-progress-label">{reviewedCount} of {sectionsCount} sections done</span>
      </div>

      <form onSubmit={onSubmit}>
        {/* ===== Cover fields — pre-filled where possible ===== */}
        <div className="cpp-quick-fill">
          <h4>Project basics</h4>
          <p>We've pre-filled what we can from the site. Check and adjust.</p>
        </div>

        {template.fields.map((f) => (
          <div key={f.key} className={`field${f.required ? ' needs-input' : ''}`}>
            <label htmlFor={`f-${f.key}`}>{f.label}{f.required ? ' *' : ''}</label>
            <input
              id={`f-${f.key}`}
              type={f.type === 'date' ? 'date' : 'text'}
              placeholder={f.placeholder}
              value={fieldValues[f.key] ?? ''}
              onChange={(e) => setField(f.key, e.target.value)}
              required={f.required}
            />
          </div>
        ))}

        {/* ===== Sections — collapsible cards with status pills ===== */}
        <h3 className="section-title" style={{ marginTop: 22 }}>Plan sections</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Tap each section. Either accept the standard text, or edit it for this specific site.
        </p>

        {template.sections.map((sec, i) => {
          const status = sectionStatuses[i];
          const isOpen = openSection === i;
          const help = helpFor(sec.heading);
          const cleanHeading = sec.heading.replace(/^\d+\.\s*/, '');
          const num = String(i + 1).padStart(2, '0');
          const statusLabel = status === 'standard' ? 'Standard text accepted'
            : status === 'edited' ? 'Edited for this site'
            : status === 'na' ? 'Not applicable'
            : 'Tap to review';
          return (
            <div key={i} className="cpp-section">
              <div
                className="cpp-section-head"
                onClick={() => setOpenSection(isOpen ? null : i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenSection(isOpen ? null : i); }}
              >
                <span className="cpp-section-num">{num}</span>
                <span className="cpp-section-title">{cleanHeading}</span>
                <span className={`cpp-section-status ${status === 'na' ? 'standard' : status}`}>{statusLabel}</span>
              </div>
              {isOpen && (
                <div className="cpp-section-body">
                  {help && (
                    <>
                      <p className="cpp-section-hint">{help.hint}</p>
                      <div className="cpp-section-example">
                        <strong>Example:</strong>{' '}
                        {help.example.split('\n').map((line, k) => (
                          <span key={k}>{line}{k < help.example.split('\n').length - 1 && <br />}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {(sec.items && sec.items.length > 0) ? (
                    <div className="rams-section-edit">
                      <div className="rams-section-head">
                        <span className="rams-section-label">Tick what applies on this site</span>
                        <span className="rams-section-pen">
                          {(sectionItemChecks[i] ?? []).filter(Boolean).length} of {(sectionItems[i] ?? []).length} ticked
                        </span>
                      </div>
                      <CppSectionTickList
                        sectionIdx={i}
                        items={sectionItems[i] ?? []}
                        checks={sectionItemChecks[i] ?? []}
                        customValue={sectionItemCustom[i] ?? ''}
                        onToggle={toggleSectionItem}
                        onRemove={removeSectionItem}
                        onAdd={addSectionItem}
                        onCustomChange={(v) => setSectionItemCustom((p) => ({ ...p, [i]: v }))}
                      />
                    </div>
                  ) : (
                    <div className="rams-section-edit">
                      <div className="rams-section-head">
                        <span className="rams-section-label">Write this for the site</span>
                        <span className="rams-section-pen">✎ Required</span>
                      </div>
                      {sec.hint && <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>{sec.hint}</p>}
                      <div className="input-with-mic">
                        <AutoTextarea
                          minRows={6}
                          placeholder={help?.example ?? ''}
                          value={sectionBodies[i] ?? ''}
                          onChange={(e) => setSectionBody(i, e.target.value)}
                        />
                        <DictateButton
                          onTranscript={(t) => setSectionBody(i, ((sectionBodies[i] ?? '') + ' ' + t).trim())}
                        />
                      </div>
                    </div>
                  )}
                  <div className="cpp-section-actions">
                    {(sec.items && sec.items.length > 0) ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => markStandard(i)}
                      >
                        ✓ Ticked items apply — move on
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={!(sectionBodies[i] ?? '').trim()}
                        onClick={() => markStandard(i)}
                      >
                        ✓ Done — move on
                      </button>
                    )}
                    {i > 0 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => markNa(i)}
                      >
                        Not applicable to this project
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOpenSection(i + 1 < sectionsCount ? i + 1 : null)}
                    >
                      Next section →
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="cpp-notes">Anything else? (optional)</label>
          <div className="input-with-mic">
            <AutoTextarea
              id="cpp-notes"
              minRows={3}
              placeholder="e.g. neighbours notified about deliveries, council parking dispensation in place"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setNotes((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Pre-issue review</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Tick what you've personally verified. Tickmarks are baked into the PDF.
        </p>
        <div className="check-list">
          {REVIEW_CHECKS.map((label, i) => (
            <label key={i} className="check-row">
              <input
                type="checkbox"
                checked={reviewChecks[i]}
                onChange={(e) => {
                  const next = reviewChecks.slice();
                  next[i] = e.target.checked;
                  setReviewChecks(next);
                }}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Issue & sign</h3>
        <div className="field needs-input">
          <label htmlFor="cpp-by">Signed by *</label>
          <input
            id="cpp-by"
            type="text"
            value={signatoryName}
            onChange={(e) => setSignatoryName(e.target.value)}
            required
          />
        </div>
        <SignaturePad ref={sigRef} label="Sign as Principal Contractor" />

        {!allSectionsDone && (
          <p className="muted" style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--warn)' }}>
            ⚠ {sectionsCount - reviewedCount} section{sectionsCount - reviewedCount === 1 ? '' : 's'} still to review.
            You can still issue: unreviewed tick-list sections use the ticked standard clauses.
          </p>
        )}

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Generating…' : (supersedeId ? 'Issue new version' : 'Generate & save CPP (Construction Phase Plan)')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav('/documents')}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
